import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingLayout } from '../layouts/MarketingLayout';
import { Button } from '../components/ui/button';
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    name: 'Pay Per Check',
    price: '9',
    period: '/bid check',
    description: 'For contractors checking occasional bids before submission.',
    features: [
      'Instant bid check with decision output',
      'Before/After impact scenarios',
      'Recommended price adjustment',
      'AI commercial explanation',
      'No commitment — pay as you go',
    ],
    cta: 'Start Checking',
    highlighted: false,
  },
  {
    name: 'Professional',
    price: '149',
    period: '/month',
    description: 'Unlimited checks for teams pricing regularly.',
    badge: 'Best Value',
    features: [
      'Unlimited bid checks',
      'Everything in Pay Per Check',
      'Full cost breakdown analysis',
      'Portfolio margin dashboard',
      'Historical trend tracking',
      'Director-ready PDF reports',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: null,
    period: '',
    description: 'For larger subcontractors or multi-division firms.',
    features: [
      'Multi-user access',
      'Custom margin thresholds',
      'Data export capability',
      'Dedicated onboarding call',
      'Custom reporting options',
    ],
    cta: 'Contact Us',
    highlighted: false,
  },
];

export default function PricingPage() {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleCheckout = async (plan) => {
    setLoadingPlan(plan);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/api/billing/public-checkout?plan=${plan}&origin_url=${encodeURIComponent(originUrl)}`);
      if (res.data.checkout_url) {
        window.top.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error('Failed to start checkout — please try again');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <MarketingLayout>
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-5"
              style={{ fontFamily: 'Barlow Condensed' }}
              data-testid="pricing-headline"
            >
              CHECK ONE BID OR<br />
              CHECK THEM ALL
            </h1>
            <p className="text-base lg:text-lg text-slate-500 max-w-xl mx-auto">
              Pay per check or go unlimited. No long-term contracts. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`
                  relative rounded-sm border p-7 flex flex-col
                  ${plan.highlighted
                    ? 'border-orange-500 ring-1 ring-orange-500/20 shadow-lg'
                    : 'border-slate-200'}
                `}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-sm">
                    {plan.badge}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </div>

                <div className="mb-7">
                  {plan.price ? (
                    <>
                      <span className="text-4xl font-mono font-bold text-slate-900">£{plan.price}</span>
                      <span className="text-slate-500 text-sm ml-1">{plan.period}</span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>Custom</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.price && plan.name !== 'Enterprise' ? (
                  <Button
                    onClick={() => handleCheckout(plan.name === 'Pay Per Check' ? 'single' : 'pro')}
                    disabled={loadingPlan !== null}
                    className={`w-full ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  >
                    {loadingPlan === (plan.name === 'Pay Per Check' ? 'single' : 'pro') ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {plan.cta}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Link to={plan.price ? '/check' : '/audit/free-bid-audit'}>
                    <Button
                      className={`w-full ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                      data-testid={`pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                    >
                      {plan.cta}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-400 mt-8">
            All prices in GBP. VAT may apply.
          </p>
        </div>
      </section>

      {/* Free Audit Bridge */}
      <section className="py-16 lg:py-20 bg-slate-50 border-t border-slate-200" data-testid="pricing-audit-bridge">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            NOT SURE YET? TRY IT FREE.
          </h2>
          <p className="text-base text-slate-500 leading-relaxed mb-8">
            Run a quick bid check right now — no account needed. See the decision output
            before committing to anything.
          </p>
          <Link to="/check">
            <Button className="btn-primary text-base px-10" data-testid="pricing-free-audit-btn">
              Run a Free Bid Check
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12" style={{ fontFamily: 'Barlow Condensed' }}>
            FREQUENTLY ASKED QUESTIONS
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'Can I try before I commit?',
                a: 'Yes. Start with a free Bid Margin Audit — no account needed. If you want ongoing access, sign up for a plan anytime.',
              },
              {
                q: 'Can I change plans later?',
                a: 'Absolutely. Upgrade or downgrade anytime from your billing settings.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit and debit cards via Stripe.',
              },
              {
                q: 'Is my data secure?',
                a: 'Yes. All data is encrypted, isolated to your organisation, and never shared without consent.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-slate-50 rounded-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
